import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  Assignment,
  AssignmentType,
} from '../../../../models/assignment.school.model';
import { Group } from '../../../../models/group.model';
import { Lesson } from '../../../../models/lesson.school.model';
import { SchoolAssignmentService } from '../../../../services/assignment.school.service';
import { AuthService } from '../../../../services/auth.service';
import { UtilsService } from '../../../../services/utils.service';
import * as assignmentActions from '../../../../state/actions/assignment.school.actions';
import { AppState } from '../../../../state/appstate';
import * as assignmentSelector from '../../../../state/selectors/assignment.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as lessonSelector from '../../../../state/selectors/lesson.school.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'app-edit-assignment',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnSubmitComponent,
    ReactiveFormsModule,
    AsyncPipe,
    JsonPipe,
    S3DocumentUploadComponent,
  ],
  templateUrl: './edit-assignment.component.html',
  styleUrl: './edit-assignment.component.css',
})
export class EditAssignmentComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  logger: NGXLogger = inject(NGXLogger);
  assignmentService: SchoolAssignmentService = inject(SchoolAssignmentService);

  @ViewChild('uploader') uploader: S3DocumentUploadComponent | undefined;

  pageForm: FormGroup;
  private dialog: MatDialog = inject(MatDialog);

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  assignmentId: string | null =
    this.route.snapshot.paramMap.get('assignmentId');

  assignment: Assignment | null | undefined;
  group: Group | null | undefined;

  lessons: Lesson[] | null | undefined;

  preSignedUrlEndpoint: string = environment.AWS_SCHOOL_ASSIGNMENTS_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  cloudFrontDomain = environment.AWS_GROUPS_CLOUDFRONT;

  moment = moment;
  assignmentType = AssignmentType;

  progress: number = 0;
  message: string = '';

  loading$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private auth: AuthService,
    private toastr: ToastrService,
  ) {
    this.pageForm = new FormGroup({
      endDate: new FormControl(
        this.assignment?.endDate
          ? dayjs(this.assignment.endDate?.toDate()).format('YYYY-MM-DD')
          : '',
        [Validators.required],
      ),
      lessonId: new FormControl(this.assignment?.lessonId),
      lessonName: new FormControl(this.assignment?.lessonName),
      type: new FormControl(this.assignment?.type.toString()),
    });

    this.loading$ = this.store.select(assignmentSelector.inProgress);
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
      this.assignmentId = params.get('assignmentId');

      this.logger.debug(
        `Captured IDs: Group: ${this.groupId}, Assignment: ${this.assignmentId}`,
      );

      if (this.groupId && this.assignmentId) {
        this.loadData();
      }
    });
  }

  private loadData() {
    // Move your store selection logic here so it only triggers
    // after we are 100% sure we have IDs
    this.store
      .select(groupSelector.getGroupById(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.fetchAssignment(group?.academicYear);
      });
  }

  private fetchAssignment(academicYear?: number) {
    this.store
      .select(
        assignmentSelector.getAssignmentById({
          groupId: this.groupId,
          assignmentId: this.assignmentId,
          academicYear: academicYear,
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((assignment) => {
        this.assignment = assignment;
        this.logger.debug(
          `EditAssignmentComponent :: ngOnInit: AssignmentId: ${assignment?.id}, GroupId: ${this.groupId}`,
        );
        if (assignment) {
          this.pageForm.patchValue({
            endDate: dayjs(assignment.endDate?.toDate()).format('YYYY-MM-DD'),
            lessonId: assignment.lessonId,
            lessonName: assignment.lessonName,
            type: assignment.type.toString(),
          });

          this.store
            .select(
              lessonSelector.getLessonsByGradeId({
                gradeId: this.assignment?.gradeId,
                groupId: this.groupId,
              }),
            )
            .pipe(takeUntil(this.exit$))
            .subscribe((lessons) => {
              this.lessons = lessons;
            });
        }

        this.metadata = {
          assignmentId: this.assignmentId,
          academicYear: academicYear,
          groupId: this.groupId,
          gradeId: this.assignment?.gradeId,
        };
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onChangeLesson(event: any): void {
    const value = event.target.value;
    this.setLesson(value);
  }

  setLesson(value: string | undefined) {
    const lesson = this.lessons?.find((f) => f.id === value) ?? undefined;
    this.pageForm.patchValue({ lessonId: lesson?.id ?? this.lessons?.[0].id });
    this.pageForm.patchValue({
      lessonName: lesson?.lessonName ?? this.lessons?.[0].lessonName,
    });
  }

  async update() {
    if (this.pageForm.invalid) return;

    this.loading$ = of(true);

    if (
      this.pageForm.get('type')?.value === AssignmentType.pdf &&
      this.uploader?.getIsAttached()
    ) {
      this.uploader?.setMetadata(this.metadata);

      this.uploader
        ?.upload()
        ?.then((result) => {
          if (result?.successful) {
            this.createDatabaseEntry(
              this.assignmentId!,
              result.successful[0]?.response?.uploadURL,
            );
          } else {
            this.loading$ = of(false);
            this.toastr.error(
              `Failed to upload document. ${result?.failed}`,
              'Document Upload Error',
            );
          }
        })
        .catch((error) => {
          this.loading$ = of(false);
          this.onShowAlert(error as AppExceptionModel);
        });
    } else {
      await this.createDatabaseEntry(this.assignmentId!, undefined);
      this.loading$ = of(false);
    }
  }

  async createDatabaseEntry(id: string, url?: string | undefined) {
    try {
      let data: any = {
        ...this.assignment,
        endDate: Timestamp.fromDate(
          moment(this.pageForm.get('endDate')?.value).toDate(),
        ),
        lessonId: this.pageForm.get('lessonId')?.value,
        lessonName: this.pageForm.get('lessonName')?.value,
      };

      if (url)
        data.assignmentUrl = UtilsService.replaceDomain(
          url,
          this.cloudFrontDomain,
        );

      await this.assignmentService.update(
        this.groupId!,
        id,
        data as Assignment,
      );

      this.store.dispatch(
        assignmentActions.updateSuccess({
          assignment: {
            ...data,
            id: id,
            created: Timestamp.now(),
            modified: Timestamp.now(),
          } as Assignment,
        }),
      );
      this.router.navigate(['../../'], {
        relativeTo: this.route,
        fragment: 'assignments',
      });
    } catch (error) {
      this.onShowAlert(error as AppExceptionModel);
      this.loading$ = of(false);
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title ?? 'Error Occurred',
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
